import * as React from 'react';
import { Modal, ModalProps } from 'reactstrap';

export interface DraggableModalProps extends ModalProps {
  /**
   * Called when the dialog is unmounted while still open, which happens when the user leaves the page through the
   * side menu with the dialog on screen (nothing stops them: there is no backdrop). Use it to reset the open state
   * in the store, otherwise the dialog comes back by itself the next time the page mounts.
   */
  onUnmountWhileOpen?: () => void;
}

export interface Translation {
  x: number;
  y: number;
}

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

// `|| 0` folds the -0 that Math.max can produce into 0, so translations compare and print as plain numbers.
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value)) || 0;

/**
 * Clamps a wanted translation so the whole dialog stays inside the viewport: no edge can be pushed past the window.
 * When the dialog is wider or taller than the viewport, the left and top edges win, so the header is always on screen.
 *
 * `base` is the dialog's box at translation (0, 0).
 */
export const clampTranslation = (wanted: Translation, base: Box, viewport: Size): Translation => {
  const minX = -base.left;
  const maxX = Math.max(minX, viewport.width - base.left - base.width);
  const minY = -base.top;
  const maxY = Math.max(minY, viewport.height - base.top - base.height);
  return { x: clamp(wanted.x, minX, maxX), y: clamp(wanted.y, minY, maxY) };
};

const ZERO: Translation = { x: 0, y: 0 };
const HANDLE_SELECTOR = '.modal-header';
const CONTROL_SELECTOR = 'button, a, input, select, textarea, [role="button"]';
const DRAGGING_CLASS = 'modal-dialog--dragging';
const noop = () => undefined;

const joinClasses = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

/**
 * A reactstrap `Modal` that behaves like a floating panel:
 *  - no backdrop, and the page underneath keeps working (pointer events pass through the modal layer), so the user
 *    can read, scroll and select text in the table behind the dialog while it is open;
 *  - it closes only through its own controls (the header cross or a footer button): no outside click, no Escape;
 *  - the header is a drag handle: hold it to move the dialog anywhere inside the viewport;
 *  - the position resets each time the dialog opens (reactstrap unmounts the dialog element on close);
 *  - leaving the page with the dialog open reports it through `onUnmountWhileOpen`, so the owner can close it in
 *    the store and it does not reappear on the way back.
 *
 * Drop-in for `Modal`: every prop is forwarded. Children stay the usual `ModalHeader` / `ModalBody` / `ModalFooter`,
 * so Bootstrap's scrollable-modal flex chain is untouched. The drag listens on the `.modal-dialog` element itself
 * (reactstrap forwards unknown props to it) and only reacts to presses that start on the `.modal-header` and not on
 * a control inside it. Moves and releases are tracked on `window`, not through pointer capture, so the drag survives
 * fast pointers leaving the header and synthetic pointers in tests.
 */
export const DraggableModal: React.FC<DraggableModalProps> = ({ className, modalClassName, isOpen, onUnmountWhileOpen, children, ...rest }) => {
  const translation = React.useRef<Translation>(ZERO);
  const dialogElement = React.useRef<HTMLElement | null>(null);
  const stopDragging = React.useRef<() => void>(noop);
  // Latest values for the unmount effect, which is registered once.
  const latest = React.useRef({ isOpen, onUnmountWhileOpen });
  latest.current = { isOpen, onUnmountWhileOpen };

  const reset = React.useCallback(() => {
    stopDragging.current();
    translation.current = ZERO;
    if (dialogElement.current) {
      dialogElement.current.style.transform = '';
      dialogElement.current.style.transition = '';
      dialogElement.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  React.useEffect(() => {
    const dragging = stopDragging;
    const state = latest;
    return () => {
      dragging.current();
      if (state.current.isOpen && state.current.onUnmountWhileOpen) {
        state.current.onUnmountWhileOpen();
      }
    };
  }, []);

  const handlePointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button > 0) {
      return;
    }
    const target = event.target as HTMLElement;
    const dialog = event.currentTarget;
    const handle = target.closest(HANDLE_SELECTOR);
    if (!handle || target.closest(CONTROL_SELECTOR)) {
      return;
    }
    event.preventDefault();

    const from = translation.current;
    const rect = dialog.getBoundingClientRect();
    const base: Box = { left: rect.left - from.x, top: rect.top - from.y, width: rect.width, height: rect.height };
    const start = { x: event.clientX, y: event.clientY };
    dialogElement.current = dialog;
    dialog.classList.add(DRAGGING_CLASS);

    const move = (e: PointerEvent) => {
      const wanted = { x: from.x + e.clientX - start.x, y: from.y + e.clientY - start.y };
      const next = clampTranslation(wanted, base, { width: window.innerWidth, height: window.innerHeight });
      translation.current = next;
      // Bootstrap animates `.modal-dialog` with a transform transition on open; without this the dialog lags the pointer.
      dialog.style.transition = 'none';
      dialog.style.transform = `translate(${next.x}px, ${next.y}px)`;
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      dialog.classList.remove(DRAGGING_CLASS);
      stopDragging.current = noop;
    };
    stopDragging.current = stop;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };

  return (
    <Modal
      {...rest}
      isOpen={isOpen}
      backdrop={false}
      keyboard={false}
      className={joinClasses(className, 'modal-dialog--draggable')}
      modalClassName={joinClasses(modalClassName, 'modal--passthrough')}
      onPointerDown={handlePointerDown}
    >
      {children}
    </Modal>
  );
};
